import React from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  PersonRounded,
  LocalHospitalRounded,
  HealingRounded,
  MedicationRounded,
  WarningAmberRounded,
  PeopleRounded,
} from '@mui/icons-material';
import { format, parseISO, isValid } from 'date-fns';
import { CARE_LEVEL_DISPLAY } from 'features/careStart/careLevelDisplay';
import { getCapabilityLabel } from 'constants/diagnosticCapabilities';
// Shared with the block this same record is rendered into for the LLM, so the screen and the
// prompt can never disagree about what the patient's record says.
import { ageFromDob, decapitalize, sexLabel, weightLabel } from 'utils/patientContext';
// Only hasItems is still needed here: the name-comparison helpers went with the
// intake block that used them.
import { hasItems } from './recordSummaryHelpers';
// The pre-visit check-in block, shared with the clinic's own inbox so the two
// cannot describe the same intake differently.
import VisitStartIntakeSummary from './VisitStartIntakeSummary';

/**
 * The one rendering of a redeemed patient record.
 *
 * Two flows redeem a share code and each used to show a different half of it. The public
 * /share landing page rendered the record sections but not the CareStart assessment; the
 * in-app import dialog rendered the CareStart assessment and, for the record itself, only
 * the sentence "Includes Allergies, Medications and Conditions" — the section NAMES, never
 * the allergen. A clinician could import a penicillin-anaphylactic patient and be told
 * "Includes Allergies" and nothing more, then wonder why the AI refused to prescribe
 * amoxicillin. Both flows now render this.
 *
 * Laid out as one outlined Paper wrapping a divided List, mirroring HealthRecordsHub, so the
 * provider reads the record in the same order and the same shape the patient maintains it in.
 *
 * TOP TO BOTTOM: today's check-in, then the chart, then the CareStart assessment. The
 * check-in leads because it is why the patient is in the room, and because it needs to
 * know what the chart is about to print in order to not print it twice — see
 * VisitStartIntakeSummary. Nothing here should describe its own position in copy: the
 * order has changed once already and the "their answers are below" caption survived it.
 *
 * Sections come in two shapes and must not be rendered alike. FIELD sections (demographics,
 * other history) are label/value pairs and read as a two-column table. ITEM sections
 * (conditions, surgery, medications, allergies) are a list of things, each with an optional
 * inline qualifier and optional free-text notes. An earlier pass joined every value with a
 * single "·" and produced lines like "Appendectomy · 2008 · No complications" and
 * "Jun 13, 1985 · male · 222", where the separator carried no meaning and the reader had to
 * guess what 2008 and 222 were. Qualifiers now read as language and prose gets its own line.
 *
 * @param {object} result - payload from redeemHealthRecordShareToken /
 *   consumeShareCodeAndFetchRecord: { patientName, patientDob, allowedSections, record,
 *   careStartResult }
 */

// Labels, icons, colors and ORDER are deliberately identical to CATEGORIES in
// components/dashboard/HealthRecordsHub.jsx. Keep the two in step: the patient builds their
// record there, and a provider reading it back in a different order has to re-find their
// place. (HealthRecordsHub calls the last section `social`; the share payload calls it
// `other`. Same section.)
const SECTIONS = [
  { id: 'demographics', label: 'Demographics', icon: PersonRounded, color: 'primary' },
  { id: 'conditions', label: 'Medical Conditions', icon: LocalHospitalRounded, color: 'info' },
  { id: 'surgical', label: 'Surgical History', icon: HealingRounded, color: 'error' },
  { id: 'medications', label: 'Medications', icon: MedicationRounded, color: 'success' },
  { id: 'allergies', label: 'Allergies', icon: WarningAmberRounded, color: 'warning' },
  { id: 'other', label: 'Other History', icon: PeopleRounded, color: 'secondary' },
];

const SECTION_META = Object.fromEntries(SECTIONS.map((s) => [s.id, s]));
const SECTION_ORDER = SECTIONS.map((s) => s.id);

const OTHER_FIELDS = [
  { key: 'smokingStatus', label: 'Smoking' },
  { key: 'alcoholUse', label: 'Alcohol use' },
  { key: 'drugUse', label: 'Drug use' },
  { key: 'occupation', label: 'Occupation' },
  { key: 'familyHistory', label: 'Family history' },
];

const formatDob = (dob) => {
  if (!dob) return null;
  const parsed = parseISO(dob);
  return isValid(parsed) ? format(parsed, 'MMM d, yyyy') : dob;
};

const joinSentences = (...values) => {
  const text = values
    .filter(Boolean)
    .map((v) => String(v).trim())
    .filter(Boolean)
    .map((v) => (/[.!?]$/.test(v) ? v : `${v}.`))
    .join(' ');
  return text || null;
};

/**
 * Every entry is a name, an optional qualifier that reads as language beside it, and optional
 * prose that gets its own line. Allergy entries arrive as plain strings on older patient
 * records and as objects on current ones, with the allergen under either key.
 */
const allergyEntry = (a) => {
  if (typeof a === 'string') return { name: a };
  return {
    name: a?.allergen || a?.name || 'Unspecified allergy',
    qualifier: a?.reaction ? `(${decapitalize(String(a.reaction).trim())})` : null,
    note: joinSentences(a?.notes),
  };
};

const sectionEntries = (id, record, { fallbackDob = null } = {}) => {
  switch (id) {
    case 'demographics': {
      const d = record.demographics || {};
      // Date of birth belongs with the rest of the demographics rather than floating in the
      // header. The share token carries its own copy alongside the patient name, which is
      // what shows when the record itself has no demographics block.
      const dob = d.dateOfBirth || fallbackDob;
      const age = ageFromDob(dob);
      const rows = [
        dob && {
          label: 'Date of birth',
          value: `${formatDob(dob)}${age != null ? ` (age ${age})` : ''}`,
        },
        d.sex && { label: 'Sex', value: sexLabel(d.sex) },
        weightLabel(d.weight) && { label: 'Weight', value: weightLabel(d.weight) },
      ].filter(Boolean);
      return rows.length ? { kind: 'fields', rows } : null;
    }
    case 'conditions':
      if (!hasItems(record.conditions)) return null;
      return {
        kind: 'items',
        rows: record.conditions.map((c) =>
          typeof c === 'string'
            ? { name: c }
            : { name: c.condition || c.name || c.diagnosis, note: joinSentences(c.notes) },
        ),
      };
    case 'surgical':
      if (!hasItems(record.surgical)) return null;
      return {
        kind: 'items',
        rows: record.surgical.map((s) =>
          typeof s === 'string'
            ? { name: s }
            : {
                name: s.procedure || s.name,
                qualifier: s.year ? `in ${s.year}` : null,
                note: joinSentences(s.notes),
              },
        ),
      };
    case 'medications':
      if (!hasItems(record.medications)) return null;
      return {
        kind: 'items',
        rows: record.medications.map((m) => ({
          name: m.name || m.medicationName,
          qualifier:
            [m.dosage, m.frequency ? decapitalize(String(m.frequency).trim()) : null]
              .filter(Boolean)
              .join(', ') || null,
        })),
      };
    // The one section that still renders when it is empty. A shared-and-empty allergy list
    // is real information a prescriber can act on, and a hidden row would be
    // indistinguishable from a section the patient chose not to share.
    case 'allergies':
      if (!hasItems(record.allergies)) return { kind: 'items', rows: [{ name: 'No known allergies' }] };
      return { kind: 'items', rows: record.allergies.map(allergyEntry) };
    case 'other': {
      const o = record.other;
      if (!o) return null;
      const rows = OTHER_FIELDS.map((f) => (o[f.key] ? { label: f.label, value: o[f.key] } : null))
        .filter(Boolean);
      return rows.length ? { kind: 'fields', rows } : null;
    }
    default:
      return null;
  }
};

/** Label/value pairs, aligned in two columns so no separator character is needed at all. */
const FieldRows = ({ rows }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 7.5rem) 1fr',
      columnGap: 2,
      rowGap: 0.5,
    }}
  >
    {rows.map((r) => (
      <React.Fragment key={r.label}>
        <Typography variant="body2" color="text.secondary">
          {r.label}
        </Typography>
        <Typography variant="body2">{r.value}</Typography>
      </React.Fragment>
    ))}
  </Box>
);

/** A list of things: name, a qualifier that reads as language, and prose on its own line. */
const ItemRows = ({ rows }) => (
  <Box>
    {rows.map((r, i) => (
      <Box key={i} sx={{ mt: i > 0 ? 1 : 0 }}>
        <Typography variant="body2">
          <Box component="span" sx={{ fontWeight: 600 }}>
            {r.name}
          </Box>
          {r.qualifier && (
            <Box component="span" sx={{ color: 'text.secondary' }}>
              {` ${r.qualifier}`}
            </Box>
          )}
        </Typography>
        {r.note && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {r.note}
          </Typography>
        )}
      </Box>
    ))}
  </Box>
);

const SectionRow = ({ id, entries }) => {
  const theme = useTheme();
  const meta = SECTION_META[id];
  const Icon = meta.icon;
  const palette = theme.palette[meta.color] ?? theme.palette.primary;

  return (
    <ListItem alignItems="flex-start" sx={{ py: 1.75, px: 2 }}>
      <ListItemIcon sx={{ minWidth: 44, mt: 0.25 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1.5,
            bgcolor: alpha(palette.main, 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon sx={{ fontSize: 20, color: `${meta.color}.main` }} />
        </Box>
      </ListItemIcon>
      <ListItemText
        disableTypography
        primary={
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
            {meta.label}
          </Typography>
        }
        secondary={
          entries.kind === 'fields' ? (
            <FieldRows rows={entries.rows} />
          ) : (
            <ItemRows rows={entries.rows} />
          )
        }
      />
    </ListItem>
  );
};

/**
 * What the patient chose not to share.
 *
 * A withheld section is not an empty one, and the difference matters for every section, not
 * just allergies: a record showing no medications because the patient withheld the list looks
 * exactly like a record showing no medications because they take none. One muted line rather
 * than a row per gap, so consent stays legible without competing with the record itself.
 */
const WithheldSections = ({ ids }) => {
  if (ids.length === 0) return null;
  return (
    <Typography variant="caption" color="text.secondary">
      Not shared: {ids.map((id) => SECTION_META[id].label).join(', ')}. Please ask the patient
      directly about anything you need.
    </Typography>
  );
};

const topConditionNames = (ddx, limit = 5) => {
  if (!ddx) return [];
  const lists = [ddx.diagnoses, ddx.primary_diagnoses, ddx.alternative_diagnoses];
  const names = [];
  for (const list of lists) {
    if (Array.isArray(list)) {
      for (const d of list) {
        const name = d?.name || d?.condition;
        if (name && !names.includes(name)) names.push(name);
      }
    }
  }
  return names.slice(0, limit);
};

/**
 * The patient's own pre-visit self-assessment. Kept visually distinct from the chart record
 * above it and labelled as patient-reported, mirroring how the LLM prompt separates the two:
 * the chart is authoritative, this is the patient's account.
 */
const CareStartSummary = ({ careStartResult }) => {
  if (!careStartResult) return null;
  const conditions = topConditionNames(careStartResult.ddxResult);
  const severity = CARE_LEVEL_DISPLAY[careStartResult.careLevel]?.muiColor || 'info';

  return (
    <Stack spacing={1.5}>
      <Alert severity={severity} icon={false}>
        <AlertTitle sx={{ fontWeight: 700 }}>Patient's CareStart self-assessment</AlertTitle>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Completed by the patient before this visit. Patient-reported, not a diagnosis.
        </Typography>
        {careStartResult.patientAge != null && (
          <Typography variant="body2">Age: {careStartResult.patientAge}</Typography>
        )}
        {careStartResult.careLevel && (
          <Typography variant="body2">
            Suggested care level:{' '}
            {CARE_LEVEL_DISPLAY[careStartResult.careLevel]?.label || careStartResult.careLevel}
          </Typography>
        )}
        {careStartResult.actionGuidance && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {careStartResult.actionGuidance}
          </Typography>
        )}
        {conditions.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
              Conditions considered
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {conditions.map((c) => (
                <Chip key={c} label={c} size="small" variant="outlined" />
              ))}
            </Stack>
          </Box>
        )}
        {careStartResult.diagnosticNeeds?.length > 0 && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Suggested tests:</strong>{' '}
            {careStartResult.diagnosticNeeds.map((k) => getCapabilityLabel(k) || k).join(', ')}
          </Typography>
        )}
      </Alert>

      {careStartResult.finalTranscript?.trim() && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
            Symptoms the patient reported
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
            {careStartResult.finalTranscript.trim()}
          </Typography>
        </Paper>
      )}
    </Stack>
  );
};

const SharedRecordSummary = ({ result, showPatientHeader = true }) => {
  if (!result) return null;

  const record = result.record || {};
  const allowedSections = result.allowedSections || [];

  const rows = SECTION_ORDER.map((id) => ({
    id,
    entries: allowedSections.includes(id)
      ? sectionEntries(id, record, { fallbackDob: result.patientDob })
      : null,
  })).filter((r) => r.entries);

  const withheld = SECTION_ORDER.filter((id) => !allowedSections.includes(id));

  // A patient who shared NO sections did not withhold six things; they made one
  // decision, or they have no record at all. A VisitStart guest is the second case:
  // no account, so nothing to share but the check-in they just answered. Listing
  // all six as withheld, above an "the sections they selected are empty" warning,
  // reads as a record full of refusals sitting on top of a perfectly good intake.
  const sharedNoRecord = allowedSections.length === 0;
  const intake = result.visitStartIntake || null;

  return (
    <Stack spacing={2}>
      {/* Only when there is a name to show. Both dialogs that wrap this are titled "Patient
          record", and most patients have no first/last name on file, so a fallback here
          printed those same two words twice, one under the other. */}
      {showPatientHeader && result.patientName && (
        <Typography variant="h6" fontWeight={700}>
          {result.patientName}
        </Typography>
      )}

      {/* Above the record: today's reason for the visit comes before the history
          the patient walked in with. It also has to render before the chart to
          know what the chart is about to show, so it can drop what would be a
          second copy — see VisitStartIntakeSummary. */}
      <VisitStartIntakeSummary
        intake={intake}
        record={record}
        allowedSections={allowedSections}
        fallbackDob={result.patientDob}
      />

      {rows.length > 0 && (
        <Paper variant="outlined">
          <List disablePadding>
            {rows.map((r, i) => (
              <React.Fragment key={r.id}>
                {i > 0 && <Divider component="li" />}
                <SectionRow id={r.id} entries={r.entries} />
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}

      {rows.length === 0 && !sharedNoRecord && (
        <Alert severity="info">
          This patient shared their record, but the sections they selected are empty.
        </Alert>
      )}

      {/* No longer says "their answers are below" — the check-in renders above
          this now, and a caption that names a direction is a caption that breaks
          the next time the order changes. */}
      {sharedNoRecord && intake ? (
        <Typography variant="caption" color="text.secondary">
          {intake.guest
            ? 'This patient checked in without an account, so there is no medical record to share.'
            : 'This patient chose not to share their medical record.'}
        </Typography>
      ) : null}

      {sharedNoRecord ? null : <WithheldSections ids={withheld} />}

      <CareStartSummary careStartResult={result.careStartResult} />
    </Stack>
  );
};

export default SharedRecordSummary;
